import { FC, ReactNode, useMemo } from 'react';
import { Stack, Typography, styled } from '@mui/material';

import { FocusTheme } from '@/components/Focus/configs/theme';
import { useAppSelector } from '@/redux/hooks';
import { selectVersions } from '@/redux/selectors/orderPlacementSelectors';
import { createSortedAvailChipItems } from '@/components/Focus/utils/newScheduleAvails';
import { ScheduleAvailType } from '@/redux/slices/slice_types';
import ScheduleAvailChipButton from '@/components/Focus/AvailsSchedule/ScheduleAvailChipButton';

export type IncrementalLaunchAvailChipItem = {
  label: string;
  value: string;
  isRemoved?: boolean;
};

type BaseProps = {
  label: string;
  icon: ReactNode;
  launchType: 'first-time' | 'incremental';
};

type FirstTimeProps = BaseProps & {
  launchType: 'first-time';
  items: IncrementalLaunchAvailChipItem[];
};

type IncrementalProps = BaseProps & {
  launchType: 'incremental';
  type: ScheduleAvailType | 'versions';
  addedValues: string[];
  removedValues: string[];
  allLanguagesMap?: Record<string, { label: string }>;
};

export type IncrementalLaunchAvailChipProps = FirstTimeProps | IncrementalProps;

const IncrementalLaunchAvailChip: FC<IncrementalLaunchAvailChipProps> = (props) => {
  const selectedVersions = useAppSelector(selectVersions);

  const items = useMemo(() => {
    if (props.launchType === 'first-time') {
      return props.items;
    }

    return createSortedAvailChipItems({
      addedValues: props.addedValues,
      removedValues: props.removedValues,
      type: props.type,
      allLanguagesMap: props.allLanguagesMap,
      allSelectedVersions: selectedVersions
    });
  }, [props]);

  if (props.launchType === 'first-time') {
    return <ScheduleAvailChipButton icon={props.icon} label={props.label} caption={items?.length} items={items} />;
  }

  const addedCount = props.addedValues.length;
  const removedCount = props.removedValues.length;
  const hasChanges = addedCount > 0 || removedCount > 0;

  if (!hasChanges) {
    return <StyledDash>-</StyledDash>;
  }

  return (
    <ScheduleAvailChipButton
      icon={props.icon}
      label={props.label}
      items={items}
      caption={
        <Stack direction="row" alignItems="center" gap={1}>
          {addedCount > 0 && <Typography color="primary">+{addedCount}</Typography>}
          {removedCount > 0 && <Typography color="text.primary">(-{removedCount})</Typography>}
        </Stack>
      }
    />
  );
};

export default IncrementalLaunchAvailChip;

const StyledDash = styled(Typography)`
  min-width: 150px;
  display: flex;
  width: 100%;
  justify-content: center;
  text-align: center;
  color: ${({ theme }) => (theme as FocusTheme).palette.neutral[30]};
`;
